import { Component, Input, OnInit } from '@angular/core';
import { of, switchMap, tap } from 'rxjs';
import { Page, Pageable } from 'src/app/utils/pageable';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { AuthorizationServiceProvider } from 'src/app/auth_profile/services/authorization-service.provider';
import * as LessonsV3 from '../../../model/v3/lessons';

export class WalletRow {
  date: Date;
  action: string;
  bundleName: string;
  amount: number;
  balance: number;

  constructor(date: Date, action: string, bundleName: string, amount: number) {
    this.date = date;
    this.action = action;
    this.bundleName = bundleName;
    this.amount = amount;
  }
}

@Component({
    selector: 'app-transactions',
    templateUrl: './transactions.component.html',
    styleUrls: ['./transactions.component.scss'],
    standalone: false
})
export class TransactionsComponent implements OnInit {
  pageable: Pageable = new Pageable(0, 5, ['lessonCommit.updateDate,desc']);
  availableLessons: number;
  viewRows: WalletRow[];
  allListedResults: Page<LessonsV3.LessonCommit>;
  bundlesById: Record<number, LessonsV3.LessonBundle> = {};
  isLoading = true;
  private schoolId: number;
  _langCode: string

  @Input() studentId: number;
  @Input() set langCode(langCode: string) {
    this._langCode = langCode
    this.refreshData().subscribe();
  }
  get langCode() {
    return this._langCode
  };

  constructor(
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider,
  ) {}

  ngOnInit(): void {
    this.auth.getAuthDetailsService().pipe(
      switchMap((api) => api.getSelfSchoolId()),
      tap((schoolId) => {
        this.schoolId = schoolId;
      }),
      switchMap(() => this.refreshData())
    ).subscribe();
  }

  private refreshData() {
    this.isLoading = true;
    return this.getAvailableStudentLessonBundles().pipe(
      switchMap(() => this.getCommits()),
      tap(() => {
        this.isLoading = false;
      })
    );
  }

  getAvailableStudentLessonBundles() {
    if (!this.schoolId || !this.studentId) {
      return of(null);
    }
    return this.provider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.getStudentBundles(
            this.schoolId,
            this.studentId,
            Pageable.of(0, 100, null),
            this.langCode,
            null
          )
        ),
        tap((availableBundlesPage) => {
          this.availableLessons = 0;
          this.bundlesById = {};
          availableBundlesPage?.content?.forEach(
            (b) => {
              this.availableLessons += b.available;
              this.bundlesById[b.id] = b;
            }
          );
        })
      );
  }

  getCommits() {
    if (!this.schoolId || !this.studentId) {
      return of(null);
    }
    return this.provider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.getStudentCommits(
            this.schoolId,
            this.studentId,
            this.pageable,
            this.langCode,
            null,
            null,
            null
          )
        ),
        tap((page) => {
          this.allListedResults = page;
          this.calculateViewRows();
        })
      );
  }

  calculateViewRows() {
    this.viewRows = [];
    const bundles = this.bundlesById ?? {};

    Object.values(bundles).forEach((bundle) => {
      this.viewRows.push(
        new WalletRow(
          bundle.date,
          'purchased',
          bundle.name,
          bundle.registeredLesson
        )
      );
    });

    this.allListedResults?.content?.forEach((commit) => {
      const bundleName = bundles[commit.bundle.id]?.name ?? '';
      if (commit.status === LessonsV3.LessonCommitStatus.PENDING || commit.status === LessonsV3.LessonCommitStatus.CANCELLED) {
        this.viewRows.push(
          new WalletRow(commit.createDate, 'reserved', bundleName, -1)
        );
      }
      if (commit.status === LessonsV3.LessonCommitStatus.CANCELLED) {
        this.viewRows.push(
          new WalletRow(commit.updateDate, 'cancelled', bundleName, +1)
        );
      } else if (commit.status !== LessonsV3.LessonCommitStatus.PENDING) {
        this.viewRows.push(
          new WalletRow(commit.updateDate, 'finished', bundleName, -1)
        );
      }
    });

    this.viewRows = this.viewRows.sort((l, r): number => {
      return new Date(r.date).getTime() - new Date(l.date).getTime();
    });

    let currentlyAvailable = this.availableLessons;
    this.viewRows.forEach((r) => {
      r.balance = currentlyAvailable;
      currentlyAvailable -= r.amount;
    });
  }

  onPageChange($event) {
    this.pageable = $event;
    this.refreshData().subscribe();
  }

  getActionName(viewRow: WalletRow) {
    return `COL.COURSES.CREDITS.ACTIONS.${viewRow.action.toUpperCase()}`;
  }
}
